import { AppError } from '../types/ServiceTypes'

/**
 * Log levels for error categorization
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  FATAL = 'fatal'
}

/**
 * Error context for enhanced logging
 */
interface ErrorContext {
  userId?: string
  component?: string
  action?: string
  timestamp?: Date
  sessionId?: string
  userAgent?: string
  url?: string
}

/**
 * Enhanced error handler utility for consistent error handling across services
 * Implements proper log levels and formatting
 *
 * Requirements: 7.5
 */
export class ErrorHandler {
  private static sessionId = this.generateSessionId()

  /**
   * Generate a unique session ID for tracking errors
   */
  private static generateSessionId(): string {
    return `session_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 11)}`
  }

  /**
   * Handle and log an error consistently
   */
  static handleError(error: AppError, context?: ErrorContext): void {
    this.logError(error, context)
    // In a real application, you might also send errors to a monitoring service
    this.sendToMonitoring(error, context)
  }

  /**
   * Log an error with consistent formatting and proper log levels
   */
  static logError(error: AppError, context?: ErrorContext): void {
    const enhancedContext = this.enhanceContext(context)
    const logEntry = this.formatLogEntry(error, enhancedContext)

    const logLevel = this.getLogLevel(error.type)

    switch (logLevel) {
      case LogLevel.DEBUG:
        console.debug(logEntry.message, logEntry.data)
        break
      case LogLevel.INFO:
        console.info(logEntry.message, logEntry.data)
        break
      case LogLevel.WARN:
        console.warn(logEntry.message, logEntry.data)
        break
      case LogLevel.ERROR:
        console.error(logEntry.message, logEntry.data)
        break
      case LogLevel.FATAL:
        console.error(`[FATAL] ${logEntry.message}`, logEntry.data)
        break
      default:
        console.error(logEntry.message, logEntry.data)
    }
  }

  /**
   * Get appropriate log level for error type
   */
  private static getLogLevel(errorType: AppError['type']): LogLevel {
    switch (errorType) {
      case 'validation':
        return LogLevel.WARN
      case 'network':
        return LogLevel.ERROR
      case 'permission':
        return LogLevel.WARN
      case 'system':
        return LogLevel.ERROR
      default:
        return LogLevel.ERROR
    }
  }

  /**
   * Enhance context with additional information
   */
  private static enhanceContext(context?: ErrorContext): ErrorContext {
    return {
      ...context,
      timestamp: context?.timestamp || new Date(),
      sessionId: this.sessionId,
      userAgent:
        typeof window !== 'undefined' ? window.navigator.userAgent : 'server',
      url: typeof window !== 'undefined' ? window.location.href : 'unknown'
    }
  }

  /**
   * Format log entry with consistent structure
   */
  private static formatLogEntry(error: AppError, context: ErrorContext) {
    const message = `[${error.type.toUpperCase()}] ${error.message}`

    const data = {
      error: {
        type: error.type,
        message: error.message,
        timestamp: error.timestamp,
        details: error.details
      },
      context: {
        userId: context.userId,
        component: context.component,
        action: context.action,
        sessionId: context.sessionId,
        userAgent: context.userAgent,
        url: context.url,
        timestamp: context.timestamp
      }
    }

    return { message, data }
  }

  /**
   * Send error to monitoring service (placeholder for real implementation)
   */
  private static sendToMonitoring(
    error: AppError,
    context?: ErrorContext
  ): void {
    // In a real application, this would send to services like Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error, { contexts: { custom: context } })
      console.info('Error would be sent to monitoring service in production', {
        error,
        context
      })
    }
  }

  /**
   * Create a standardized error object
   */
  static createError(
    type: AppError['type'],
    message: string,
    details?: any
  ): AppError {
    return {
      type,
      message,
      details,
      timestamp: new Date()
    }
  }

  /**
   * Wrap service operations with consistent error handling
   */
  static async wrapServiceOperation<T>(
    operation: () => Promise<T>,
    errorContext: string,
    context?: ErrorContext
  ): Promise<T> {
    try {
      return await operation()
    } catch (error) {
      const appError = this.createError(
        'system',
        `${errorContext}: ${error}`,
        error
      )
      this.handleError(appError, context)
      throw appError
    }
  }

  /**
   * Convert a generic error to an AppError
   */
  static fromError(error: any, type: AppError['type'] = 'system'): AppError {
    return this.createError(
      type,
      error.message || 'An unknown error occurred',
      error
    )
  }

  /**
   * Convenience methods for different log levels
   */
  static logDebug(
    message: string,
    details?: any,
    context?: ErrorContext
  ): void {
    const error = this.createError('system', message, details)
    const logEntry = this.formatLogEntry(error, this.enhanceContext(context))
    console.debug(logEntry.message, logEntry.data)
  }

  static logInfo(message: string, details?: any, context?: ErrorContext): void {
    const error = this.createError('system', message, details)
    const logEntry = this.formatLogEntry(error, this.enhanceContext(context))
    console.info(logEntry.message, logEntry.data)
  }

  static logWarning(
    message: string,
    details?: any,
    context?: ErrorContext
  ): void {
    const error = this.createError('validation', message, details)
    const logEntry = this.formatLogEntry(error, this.enhanceContext(context))
    console.warn(logEntry.message, logEntry.data)
  }

  /**
   * Log validation errors specifically
   */
  static logValidationError(
    message: string,
    details?: any,
    context?: ErrorContext
  ): void {
    const error = this.createError('validation', message, details)
    this.handleError(error, context)
  }

  /**
   * Log network errors specifically
   */
  static logNetworkError(
    message: string,
    details?: any,
    context?: ErrorContext
  ): void {
    const error = this.createError('network', message, details)
    this.handleError(error, context)
  }

  /**
   * Log permission errors specifically
   */
  static logPermissionError(
    message: string,
    details?: any,
    context?: ErrorContext
  ): void {
    const error = this.createError('permission', message, details)
    this.handleError(error, context)
  }

  /**
   * Log system errors specifically
   */
  static logSystemError(
    message: string,
    details?: any,
    context?: ErrorContext
  ): void {
    const error = this.createError('system', message, details)
    this.handleError(error, context)
  }
}

export default ErrorHandler
